import React, { useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import {
	IChangeDishPositionContext,
	ChangeDishPositionContext,
} from '../../../../../changeDishPositionState/changeDishPositionContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import { SHOW_ARCHIVED_CUSTOMERS, PRE_SELECTED_CUSTOMER_ID_FOR_MENU } from 'appConstants';
import { UserRoles, IGetCustomersShort, EntityStatus } from 'types';
import { getCustomersShortInfo } from 'backend/customers';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';

export const CustomerSelect: React.FC = () => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const { setIDCustomer, IDSelectedCustomer } = useContext<IChangeDishPositionContext>(ChangeDishPositionContext);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetCustomersShort, null>(
		hasAccess ? getCustomersShortInfo : null,
	);
	const [showArchived, setShowArchived] = useState<boolean>(localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true');

	const customers = useMemo(() => {
		const items = hasAccess ? response?.data.customers : loggedUser.customers || [];
		const visibleItems = showArchived
			? items
			: items?.filter(
					(customer) => customer.status === EntityStatus.Active || customer.id === IDSelectedCustomer,
			  );

		return visibleItems?.map((customer) => ({
			...customer,
			additionalContent: customer.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [hasAccess, response, loggedUser, showArchived, IDSelectedCustomer]);

	useEffect(() => {
		if (localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU) && hasAccess && isFirstRender && response) {
			setIsFirstRender(false);
			setIDCustomer(localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU));
		}
		if (
			localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU) &&
			!hasAccess &&
			isFirstRender &&
			loggedUser.customers
		) {
			setIsFirstRender(false);
			setIDCustomer(localStorage.getItem(PRE_SELECTED_CUSTOMER_ID_FOR_MENU));
		}
	}, [response, hasAccess, loggedUser.customers]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<OutsideFormSelect
			containerClassName="!min-h-min w-px"
			className="!mt-0"
			testId="CustomerSelect"
			name="CustomerSelect"
			label="customerLabel"
			onChange={setIDCustomer}
			onRefetch={refetch}
			value={IDSelectedCustomer}
			options={customers}
			listItemHeight={customers?.length}
			disabled={loading}
			loading={loading}
			fetchError={responseError}
			withValidation={false}
			keepSearchWithDropdownRender={true}
			dropdownRender={renderSelectMenu}
		/>
	);
};
